import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import LabelWithPopover from "@/components/form/labels/LabelWithPopover";
import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import StatusTag from "@/components/tags/StatusTag";
import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { PageHeader } from "@/components/views/PageHeader";
import { formatLocaleCurrency, formatLocaleDate, formatLocaleDateTime } from "@/utils/formatUtils";
import { useBackNavigation } from "@/utils/hooksUtils";
import { numberOrZero } from "@/utils/utils";
import { MenuInfo } from "@rc-component/menu/es/interface";
import {
  Alert,
  App as AntApp,
  Button,
  Card,
  Col,
  Descriptions,
  Dropdown,
  Row,
  Space,
  Steps,
  Tabs,
  Tag,
  Upload,
} from "antd";
import { ItemType } from "antd/es/menu/interface";
import { t } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import {
  useChangeCommissionsBatchStep,
  useDeleteCommissionsBatch,
  useDownloadCommissionsBatchOutputsExport,
  useImportSpecialCommissions,
} from "../../../../common/api/queries";
import { FileUploadAccept, PresetColors, rowGutter } from "../../../../common/constants";
import { Permission } from "../../../../common/security/enums";
import { useHasPermissions, useIsCurrentAgentTypePlus, useIsSystemAdmin } from "../../../auth/hooks";
import { CommissionsBatchStep, CommissionsBatchStepDirection } from "../enums";
import { COMMISSIONS_BATCH_ROUTE_PATHS } from "../paths";
import { CommissionsBatch } from "../types";
import { CommissionsBatchOutputs } from "./CommissionsBatchOutputs";
import { CommissionsBatchStepTag } from "./CommissionsBatchStepTag";
import { CommissionsBatchChangeStepModalForm } from "./forms/CommissionsBatchChangeStepModalForm";
import { CommissionsBatchPaymentOrderForm } from "./forms/CommissionsBatchPaymentOrderForm";
import { UpdateCommissionsBatchForm } from "./forms/UpdateCommissionsBatchForm";
import { CommissionBatchFinishedDateFormModal } from "./modals/CommissionBatchFinishedDateFormModal";
import { ExportInactiveAgentsFileTypeQuestionModal } from "./modals/ExportInactiveAgentsFileTypeQuestionModal";
import { ExportInputsFileTypeQuestionModal } from "./modals/ExportInputsFileTypeQuestionModal";
import { CommissionsBatchCalculationStep } from "./steps/CommissionsBatchCalculationStep";
import { CommissionsBatchFinishStep } from "./steps/CommissionsBatchFinishStep";
import { CommissionsBatchImportStep } from "./steps/CommissionsBatchImportStep";

const getTags = (batch: CommissionsBatch, operationInProgress: boolean, isTopAgentPlus?: boolean) => {
  const tags = [
    <CommissionsBatchStepTag key={1} step={batch.step} />,
    isTopAgentPlus ? (
      <Tag key={2} color={PresetColors.MAGENTA}>
        {t(`commissions.batch.enums.commissionBatchType_PLUS.${batch.type}`)}
      </Tag>
    ) : undefined,
  ];

  if (operationInProgress) {
    tags.push(<StatusTag key={3} status="processing" />);
  }

  return tags;
};

interface Props {
  batch: CommissionsBatch;
}

export const CommissionsBatchDetailView = ({ batch }: Props) => {
  const { t } = useTranslation();
  const { modal, notification } = AntApp.useApp();

  const navigate = useNavigate();

  const handleBack = useBackNavigation();

  const [batchFormOpen, setBatchFormOpen] = useState(false);
  const [isChangeStepModalOpen, setIsChangeStepModalOpen] = useState(false);
  const [paymentOrderFormOpen, setPaymentOrderFormOpen] = useState(false);
  const [isExportInputsModalOpen, setIsExportInputsModalOpen] = useState(false);
  const [isExportInactiveAgentsModalOpen, setIsExportInactiveAgentsModalOpen] = useState(false);
  const [isUpdateCommissionsBatchFinishedDateModalOpen, setIsUpdateCommissionsBatchFinishedDateModalOpen] =
    useState(false);

  const isSystemAdmin = useIsSystemAdmin();
  const isTopAgentPlus = useIsCurrentAgentTypePlus();
  const hasCommissionsManagePermission = useHasPermissions(Permission.COMMISSIONS_MANAGE);

  const downloadCommissionsBatchOutputsExport = useDownloadCommissionsBatchOutputsExport();
  const importSpecialCommissions = useImportSpecialCommissions();
  const deleteCommissionsBatch = useDeleteCommissionsBatch();
  const changeCommissionsBatchStep = useChangeCommissionsBatchStep();

  const inputImportInProgress = batch.inputs.some((a) => a.importInProgress);
  const operationInProgress = batch.stepChangeInProgress || inputImportInProgress;

  const handleStepChange = (direction: CommissionsBatchStepDirection) => {
    if (batch.step === CommissionsBatchStep.CALCULATION && direction === CommissionsBatchStepDirection.PREVIOUS) {
      modal.confirm({
        title: t("commissions.batch.helpers.returnToImportStepConfirm"),
        okText: t("common.yes"),
        cancelText: t("common.cancel"),
        onOk: () => {
          changeCommissionsBatchStep.mutate({
            id: batch.id,
            object: { optimisticLockVersion: batch.optimisticLockVersion, direction },
          });
        },
      });
    } else if (
      isTopAgentPlus &&
      batch.step === CommissionsBatchStep.IMPORT &&
      direction === CommissionsBatchStepDirection.NEXT
    ) {
      setIsChangeStepModalOpen(true);
    } else {
      changeCommissionsBatchStep.mutate({
        id: batch.id,
        object: { optimisticLockVersion: batch.optimisticLockVersion, direction },
      });
    }
  };

  const handleDropdownClick = (clickInfo: MenuInfo) => {
    switch (clickInfo.key) {
      case "edit":
        setBatchFormOpen(true);
        break;
      case "delete":
        modal.confirm({
          title: t("commissions.batch.helpers.deleteBatchConfirm"),
          okText: t("common.yes"),
          cancelText: t("common.no"),
          onOk: () =>
            deleteCommissionsBatch.mutate(batch.id, {
              onSuccess: () => navigate(COMMISSIONS_BATCH_ROUTE_PATHS.list.to, { replace: true }),
            }),
        });
        break;
      case "exportInputs":
        setIsExportInputsModalOpen(true);
        break;
      case "exportOutputs":
        downloadCommissionsBatchOutputsExport.mutate(batch.id);
        break;
      case "exportInactiveAgents":
        setIsExportInactiveAgentsModalOpen(true);
        break;
    }
  };

  const getDropdownMenuItems = (batch: CommissionsBatch, hasCommissionsManagePermission: boolean): ItemType[] => {
    const items: (ItemType & { visibility: boolean })[] = [
      {
        key: "edit",
        label: (
          <ActionTextIcon icon="edit" text={t("common.edit")} color="orange" disabled={batch.stepChangeInProgress} />
        ),
        visibility: hasCommissionsManagePermission && batch.step !== CommissionsBatchStep.FINISH,
      },
      {
        key: "delete",
        label: (
          <ActionTextIcon icon="delete" text={t("common.delete")} color="red" disabled={batch.stepChangeInProgress} />
        ),
        visibility: hasCommissionsManagePermission && batch.step !== CommissionsBatchStep.FINISH,
      },
      {
        key: "exportInputs",
        label: (
          <ActionTextIcon
            icon="download"
            text={t("commissions.batch.actions.exportInputs")}
            color="blue"
            disabled={batch.stepChangeInProgress}
          />
        ),
        visibility: true,
      },
      {
        key: "exportOutputs",
        label: (
          <ActionTextIcon
            icon="download"
            text={t("commissions.batch.actions.exportOutputs")}
            color="blue"
            disabled={batch.stepChangeInProgress}
          />
        ),
        visibility: [
          CommissionsBatchStep.RECAPITULATION,
          CommissionsBatchStep.REVIEW,
          CommissionsBatchStep.FINISH,
        ].includes(batch.step),
      },
      {
        key: "exportInactiveAgents",
        label: (
          <ActionTextIcon
            icon="download"
            text={t("commissions.batch.actions.exportInactiveAgents")}
            color="blue"
            disabled={batch.stepChangeInProgress}
          />
        ),
        visibility:
          isTopAgentPlus &&
          [CommissionsBatchStep.RECAPITULATION, CommissionsBatchStep.REVIEW, CommissionsBatchStep.FINISH].includes(
            batch.step,
          ),
      },
      {
        key: "importSpecialCommissions",
        label: (
          <Upload
            accept={FileUploadAccept.CSV}
            beforeUpload={(data) => {
              const formData = new FormData();
              formData.append("file", data);

              importSpecialCommissions.mutate(
                { id: batch.id, object: formData },
                {
                  onSuccess: () =>
                    notification.success({
                      title: t("common.operationSuccess"),
                      description: t("commissions.batch.helpers.importFinished"),
                    }),
                },
              );

              return false;
            }}
            showUploadList={false}
          >
            <ActionTextIcon
              icon="import"
              text={t("commissions.batch.actions.importSpecialCommissions")}
              color="green"
              disabled={batch.stepChangeInProgress}
            />
          </Upload>
        ),
        visibility: CommissionsBatchStep.RECAPITULATION === batch.step && hasCommissionsManagePermission,
      },
    ];

    return items
      .filter((item) => item.visibility)
      .map((item) => {
        const { visibility, ...data } = item;
        return data;
      });
  };

  const batchSteps = (
    <Steps
      size="small"
      current={Object.keys(CommissionsBatchStep).indexOf(batch.step)}
      items={[
        {
          icon: inputImportInProgress && <AntIcon type="loading" />,
          title: t("commissions.batch.enums.batchStep." + CommissionsBatchStep.IMPORT),
        },
        {
          icon: batch.upcomingStep === CommissionsBatchStep.CALCULATION && <AntIcon type="loading" />,
          title: t("commissions.batch.enums.batchStep." + CommissionsBatchStep.CALCULATION),
        },
        {
          icon: batch.upcomingStep === CommissionsBatchStep.RECAPITULATION && <AntIcon type="loading" />,
          title: t("commissions.batch.enums.batchStep." + CommissionsBatchStep.RECAPITULATION),
        },
        {
          icon: batch.upcomingStep === CommissionsBatchStep.REVIEW && <AntIcon type="loading" />,
          title: t("commissions.batch.enums.batchStep." + CommissionsBatchStep.REVIEW),
        },
        {
          icon: batch.upcomingStep === CommissionsBatchStep.FINISH && <AntIcon type="loading" />,
          title: t("commissions.batch.enums.batchStep." + CommissionsBatchStep.FINISH),
        },
      ]}
    />
  );

  const dropdownMenu = (
    <Dropdown
      trigger={["click"]}
      placement="bottomRight"
      menu={{
        items: getDropdownMenuItems(batch, hasCommissionsManagePermission),
        onClick: handleDropdownClick,
      }}
    >
      <Button icon={<AntIcon type="more" />} />
    </Dropdown>
  );

  return (
    <>
      <PageHeader
        onBack={handleBack}
        title={t("commissions.batch.titles.batchDetail", { name: batch.name })}
        tags={getTags(batch, operationInProgress, isTopAgentPlus)}
        extra={
          hasCommissionsManagePermission ? (
            batch.step === CommissionsBatchStep.FINISH ? (
              <Space>
                <SecondaryButton icon={<AntIcon type="bank" />} onClick={() => setPaymentOrderFormOpen(true)}>
                  {t("commissions.batch.actions.generatePaymentOrder")}
                </SecondaryButton>

                {dropdownMenu}
              </Space>
            ) : (
              <Space>
                <Button
                  type="primary"
                  icon={<AntIcon type="double-left" />}
                  disabled={batch.step === CommissionsBatchStep.IMPORT || operationInProgress}
                  onClick={() => handleStepChange(CommissionsBatchStepDirection.PREVIOUS)}
                >
                  {t("commissions.batch.actions.batchPreviousStep")}
                </Button>

                <Button
                  type="primary"
                  icon={<AntIcon type="double-right" />}
                  disabled={operationInProgress}
                  onClick={() => handleStepChange(CommissionsBatchStepDirection.NEXT)}
                >
                  {t("commissions.batch.actions.batchNextStep")}
                </Button>

                {(batch.step === CommissionsBatchStep.RECAPITULATION || batch.step === CommissionsBatchStep.REVIEW) && (
                  <SecondaryButton icon={<AntIcon type="bank" />} onClick={() => setPaymentOrderFormOpen(true)}>
                    {t("commissions.batch.actions.generatePaymentOrder")}
                  </SecondaryButton>
                )}

                {dropdownMenu}
              </Space>
            )
          ) : (
            <Space>
              {batchSteps}
              {dropdownMenu}
            </Space>
          )
        }
      >
        <ItemCreatedUpdatedInfoView item={batch} style={{ marginTop: -15 }} />

        {batch.calculationDate && (
          <div className="sub-header-info">
            <span>{t("commissions.batch.attrs.calculationDate")}:</span> {formatLocaleDate(batch.calculationDate)}
          </div>
        )}

        {batch.finishedAt ||
        batch.step === CommissionsBatchStep.RECAPITULATION ||
        batch.step === CommissionsBatchStep.REVIEW ||
        batch.step === CommissionsBatchStep.FINISH ? (
          <div className="sub-header-info">
            <Space>
              <span>
                <b>{t("commissions.batch.attrs.finishedAt")}:</b>{" "}
                {batch.finishedAt ? formatLocaleDateTime(batch.finishedAt) : undefined}
              </span>

              {(batch.step === CommissionsBatchStep.RECAPITULATION ||
                batch.step === CommissionsBatchStep.REVIEW ||
                batch.step === CommissionsBatchStep.FINISH) &&
              hasCommissionsManagePermission &&
              isSystemAdmin ? (
                <ActionTextIcon
                  icon="edit"
                  color="blue"
                  text={t("common.edit")}
                  disabled={batch.stepChangeInProgress}
                  onClick={() => setIsUpdateCommissionsBatchFinishedDateModalOpen(true)}
                />
              ) : undefined}
            </Space>
          </div>
        ) : undefined}

        <Row gutter={rowGutter} className="margin-top-small">
          <Col flex="auto">
            <table className="data-table-view">
              <tbody>
                <tr>
                  <td>{t("commissions.batch.attrs.allCommissionAmount")}:</td>
                  <td className="right-align">{formatLocaleCurrency(batch.allCommissionAmount ?? 0)}</td>
                </tr>
                <tr>
                  <td>
                    {t("commissions.batch.attrs.currentCommissionAmount")}:
                    <br />
                    {t("commissions.batch.attrs.previousCommissionAmount")}:
                    {isTopAgentPlus ? (
                      <>
                        <br />
                        {t("commissions.batch.helpers.previousSplitAmount")}
                        <br />
                        {t("commissions.batch.helpers.structurePreviousSplitAmount")}
                      </>
                    ) : undefined}
                    :
                  </td>
                  <td className="right-align">
                    {formatLocaleCurrency(batch.currentCommissionAmount ?? 0)}
                    <br />
                    {formatLocaleCurrency(batch.previousCommissionAmount ?? 0)}
                    <br />
                    {isTopAgentPlus ? (
                      <>
                        {formatLocaleCurrency(
                          numberOrZero(batch.totalPreviousSplitAmount30) +
                            numberOrZero(batch.totalPreviousSplitAmountFirst10) +
                            numberOrZero(batch.totalPreviousSplitAmountSecond10) +
                            numberOrZero(batch.totalPreviousSplitAmountOthers),
                        )}
                        <br />
                        {formatLocaleCurrency(
                          numberOrZero(batch.structurePreviousSplitAmount30) +
                            numberOrZero(batch.structurePreviousSplitAmountFirst10) +
                            numberOrZero(batch.structurePreviousSplitAmountSecond10) +
                            numberOrZero(batch.structurePreviousSplitAmountOthers),
                        )}
                      </>
                    ) : undefined}
                  </td>

                  <td>
                    <br />
                    {batch.step === CommissionsBatchStep.IMPORT && (
                      <ActionTextIcon
                        path={generatePath(COMMISSIONS_BATCH_ROUTE_PATHS.postponedCommissions.to, { id: batch.id })}
                        icon="interaction"
                        color="blue"
                        text={t("common.manage")}
                        disabled={batch.stepChangeInProgress}
                      />
                    )}
                    {isTopAgentPlus ? (
                      <>
                        <br />
                        <LabelWithPopover
                          style={{ marginRight: -18 }}
                          popoverContent={
                            <Descriptions
                              style={{ width: 380 }}
                              styles={{ content: { textAlign: "right" } }}
                              size="small"
                              bordered
                              title={t("commissions.batch.helpers.previousSplitTitle")}
                              items={[
                                {
                                  key: "totalPreviousSplitAmount30",
                                  label: t("commissions.batch.attrs.splitAmountRatio30"),
                                  children: formatLocaleCurrency(batch.totalPreviousSplitAmount30 ?? 0),
                                },
                                {
                                  key: "totalPreviousSplitAmountFirst10",
                                  label: t("commissions.batch.attrs.splitAmountFirstRatio10"),
                                  children: formatLocaleCurrency(batch.totalPreviousSplitAmountFirst10 ?? 0),
                                },
                                {
                                  key: "totalPreviousSplitAmountSecond10",
                                  label: t("commissions.batch.attrs.splitAmountSecondRatio10"),
                                  children: formatLocaleCurrency(batch.totalPreviousSplitAmountSecond10 ?? 0),
                                },
                                {
                                  key: "totalPreviousSplitAmountOthers",
                                  label: t("commissions.batch.attrs.splitAmountOtherRatios"),
                                  children: formatLocaleCurrency(batch.totalPreviousSplitAmountOthers ?? 0),
                                },
                              ]}
                              column={1}
                            />
                          }
                          label={""}
                        />

                        <br />
                        <LabelWithPopover
                          style={{ marginRight: -18 }}
                          popoverContent={
                            <Descriptions
                              style={{ width: 380 }}
                              styles={{ content: { textAlign: "right" } }}
                              size="small"
                              bordered
                              title={t("commissions.batch.helpers.structurePreviousSplitTitle")}
                              items={[
                                {
                                  key: "structurePreviousSplitAmount30",
                                  label: t("commissions.batch.attrs.splitAmountRatio30"),
                                  children: formatLocaleCurrency(batch.structurePreviousSplitAmount30 ?? 0),
                                },
                                {
                                  key: "structurePreviousSplitAmountFirst10",
                                  label: t("commissions.batch.attrs.splitAmountFirstRatio10"),
                                  children: formatLocaleCurrency(batch.structurePreviousSplitAmountFirst10 ?? 0),
                                },
                                {
                                  key: "structurePreviousSplitAmountSecond10",
                                  label: t("commissions.batch.attrs.splitAmountSecondRatio10"),
                                  children: formatLocaleCurrency(batch.structurePreviousSplitAmountSecond10 ?? 0),
                                },
                                {
                                  key: "structurePreviousSplitAmountOthers",
                                  label: t("commissions.batch.attrs.splitAmountOtherRatios"),
                                  children: formatLocaleCurrency(batch.structurePreviousSplitAmountOthers ?? 0),
                                },
                              ]}
                              column={1}
                            />
                          }
                          label={""}
                        />
                      </>
                    ) : undefined}
                  </td>
                </tr>
                <tr>
                  <td>
                    {t("commissions.batch.attrs.calculatedCommissionAmount")}:
                    <br />
                    {t("commissions.batch.attrs.postponedCommissionAmount")}:
                    {isTopAgentPlus ? (
                      <>
                        <br />
                        {t("commissions.batch.helpers.currentSplitAmount")}:
                        <br />
                        {t("commissions.batch.helpers.structureCurrentSplitAmount")}:
                      </>
                    ) : undefined}
                  </td>
                  <td className="right-align">
                    {formatLocaleCurrency(batch.calculatedCommissionAmount ?? 0)}
                    <br />
                    {formatLocaleCurrency(batch.postponedCommissionAmount ?? 0)}
                    <br />
                    {isTopAgentPlus ? (
                      <>
                        {formatLocaleCurrency(
                          numberOrZero(batch.totalSplitAmount30) +
                            numberOrZero(batch.totalSplitAmountFirst10) +
                            numberOrZero(batch.totalSplitAmountSecond10) +
                            numberOrZero(batch.totalSplitAmountOthers),
                        )}
                        <br />
                        {formatLocaleCurrency(
                          numberOrZero(batch.structureSplitAmount30) +
                            numberOrZero(batch.structureSplitAmountFirst10) +
                            numberOrZero(batch.structureSplitAmountSecond10) +
                            numberOrZero(batch.structureSplitAmountOthers),
                        )}
                      </>
                    ) : undefined}
                  </td>
                  <td>
                    {isTopAgentPlus ? (
                      <>
                        <br />
                        <br />
                        <LabelWithPopover
                          style={{ marginRight: -18 }}
                          popoverContent={
                            <Descriptions
                              style={{ width: 380 }}
                              styles={{ content: { textAlign: "right" } }}
                              size="small"
                              bordered
                              title={t("commissions.batch.helpers.currentSplitTitle")}
                              items={[
                                {
                                  key: "totalSplitAmount30",
                                  label: t("commissions.batch.attrs.splitAmountRatio30"),
                                  children: formatLocaleCurrency(batch.totalSplitAmount30 ?? 0),
                                },
                                {
                                  key: "totalSplitAmountFirst10",
                                  label: t("commissions.batch.attrs.splitAmountFirstRatio10"),
                                  children: formatLocaleCurrency(batch.totalSplitAmountFirst10 ?? 0),
                                },
                                {
                                  key: "totalSplitAmountSecond10",
                                  label: t("commissions.batch.attrs.splitAmountSecondRatio10"),
                                  children: formatLocaleCurrency(batch.totalSplitAmountSecond10 ?? 0),
                                },
                                {
                                  key: "totalSplitAmountOthers",
                                  label: t("commissions.batch.attrs.splitAmountOtherRatios"),
                                  children: formatLocaleCurrency(batch.totalSplitAmountOthers ?? 0),
                                },
                              ]}
                              column={1}
                            />
                          }
                          label={""}
                        />

                        <br />
                        <LabelWithPopover
                          style={{ marginRight: -18 }}
                          popoverContent={
                            <Descriptions
                              style={{ width: 380 }}
                              styles={{ content: { textAlign: "right" } }}
                              size="small"
                              bordered
                              title={t("commissions.batch.helpers.structureCurrentSplitTitle")}
                              items={[
                                {
                                  key: "structureSplitAmount30",
                                  label: t("commissions.batch.attrs.splitAmountRatio30"),
                                  children: formatLocaleCurrency(batch.structureSplitAmount30 ?? 0),
                                },
                                {
                                  key: "structureSplitAmountFirst10",
                                  label: t("commissions.batch.attrs.splitAmountFirstRatio10"),
                                  children: formatLocaleCurrency(batch.structureSplitAmountFirst10 ?? 0),
                                },
                                {
                                  key: "structureSplitAmountSecond10",
                                  label: t("commissions.batch.attrs.splitAmountSecondRatio10"),
                                  children: formatLocaleCurrency(batch.structureSplitAmountSecond10 ?? 0),
                                },
                                {
                                  key: "structureSplitAmountOthers",
                                  label: t("commissions.batch.attrs.splitAmountOtherRatios"),
                                  children: formatLocaleCurrency(batch.structureSplitAmountOthers ?? 0),
                                },
                              ]}
                              column={1}
                            />
                          }
                          label={""}
                        />
                      </>
                    ) : undefined}
                  </td>
                </tr>
                <tr>
                  <td>
                    {t("commissions.batch.attrs.bankAccountCommissionAmount")}:
                    <br />
                    {t("commissions.batch.attrs.invoiceCommissionAmount")}:
                    <br />
                    {t("commissions.batch.attrs.volumeCommissionAmount")}:
                  </td>
                  <td className="right-align">
                    {formatLocaleCurrency(batch.bankAccountCommissionAmount ?? 0)}
                    <br />
                    {formatLocaleCurrency(batch.invoiceCommissionAmount ?? 0)}
                    <br />
                    {formatLocaleCurrency(batch.volumeCommissionAmount ?? 0)}
                  </td>
                </tr>
                <tr>
                  <td>
                    {t("commissions.batch.attrs.manualBailAccountMovementsCount")}:
                    <br />
                    {t("commissions.batch.attrs.manualDebtAccountMovementsCount")}:
                  </td>
                  <td className="right-align">
                    {batch.manualBailAccountMovementsCount}
                    <br />
                    {batch.manualDebtAccountMovementsCount}
                  </td>
                  {(batch.step === CommissionsBatchStep.RECAPITULATION ||
                    batch.step === CommissionsBatchStep.REVIEW ||
                    batch.step === CommissionsBatchStep.FINISH) && (
                    <td>
                      <ActionTextIcon
                        path={generatePath(COMMISSIONS_BATCH_ROUTE_PATHS.manualBailAccountMovements.to, {
                          id: batch.id,
                        })}
                        icon="interaction"
                        color="blue"
                        text={
                          batch.step === CommissionsBatchStep.RECAPITULATION ? t("common.manage") : t("common.show")
                        }
                        disabled={batch.stepChangeInProgress}
                      />
                      <br />
                      <ActionTextIcon
                        path={generatePath(COMMISSIONS_BATCH_ROUTE_PATHS.manualDebtAccountMovements.to, {
                          id: batch.id,
                        })}
                        icon="interaction"
                        color="blue"
                        text={
                          batch.step === CommissionsBatchStep.RECAPITULATION ? t("common.manage") : t("common.show")
                        }
                        disabled={batch.stepChangeInProgress}
                      />
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
          </Col>

          <Col flex="700px" className="right-align">
            {hasCommissionsManagePermission && batchSteps}

            {batch.stepChangeError && (
              <Alert
                className="left-align margin-top-medium"
                title={t("commissions.batch.helpers.batchStepChangeError")}
                description={batch.stepChangeError.description}
                type="error"
                showIcon
              />
            )}
          </Col>
        </Row>
      </PageHeader>

      <Tabs
        activeKey={batch.step}
        className="margin-top-medium"
        renderTabBar={() => <></>}
        items={[
          {
            key: CommissionsBatchStep.IMPORT,
            label: null,
            children: <CommissionsBatchImportStep batch={batch} />,
          },
          {
            key: CommissionsBatchStep.CALCULATION,
            label: null,
            children: <CommissionsBatchCalculationStep batch={batch} />,
          },
          {
            key: CommissionsBatchStep.RECAPITULATION,
            label: null,
            children: (
              <Card className="card-box">
                <CommissionsBatchOutputs batch={batch} />
              </Card>
            ),
          },
          {
            key: CommissionsBatchStep.REVIEW,
            label: null,
            children: (
              <Card className="card-box">
                <CommissionsBatchOutputs batch={batch} />
              </Card>
            ),
          },
          {
            key: CommissionsBatchStep.FINISH,
            label: null,
            children: <CommissionsBatchFinishStep batch={batch} />,
          },
        ]}
        destroyOnHidden
      />

      <CommissionBatchFinishedDateFormModal
        batch={batch}
        isOpen={isUpdateCommissionsBatchFinishedDateModalOpen}
        onClose={() => setIsUpdateCommissionsBatchFinishedDateModalOpen(false)}
      />

      <ExportInputsFileTypeQuestionModal
        isOpen={isExportInputsModalOpen}
        onClose={() => setIsExportInputsModalOpen(false)}
        batchId={batch.id}
      />

      <ExportInactiveAgentsFileTypeQuestionModal
        isOpen={isExportInactiveAgentsModalOpen}
        onClose={() => setIsExportInactiveAgentsModalOpen(false)}
        batchId={batch.id}
      />

      <CommissionsBatchChangeStepModalForm
        isOpen={isChangeStepModalOpen}
        onCancel={() => setIsChangeStepModalOpen(false)}
        batch={batch}
      />

      <UpdateCommissionsBatchForm batch={batch} open={batchFormOpen} onFormCancel={() => setBatchFormOpen(false)} />

      <CommissionsBatchPaymentOrderForm
        batch={batch}
        open={paymentOrderFormOpen}
        onFormCancel={() => setPaymentOrderFormOpen(false)}
      />
    </>
  );
};
